import { useCallback, useRef } from 'react';
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

/**
 * A confirm or a submit with `loading={x.isPending}` only is not guarded against a double click: `isPending` is read from the last
 * render, so both clicks land before it turns true (R4b: 2 DELETEs, both 200; the second of a DELETE that works answers 404 and
 * shows an error toast over the success one; a create makes two resources because the server does not enforce unique names).
 * This is the one guard: a mutation built with {@link useOnceMutation} sends once per set of variables while a request is in
 * flight, and a second call shares the first call's promise.
 */

/**
 * What `onMutate` hands to the other callbacks. `sender`: this call is the one that sends the request and runs the caller's
 * callbacks. `context`: what the caller's own `onMutate` returned, which the caller's callbacks receive as their `context`
 * argument (unchanged: the wrapper keeps its own flag beside it, not inside it).
 */
export interface OnceCall<TContext = unknown> {
  sender: boolean;
  context: TContext | undefined;
  /** The claim this call holds (a sender with a key), so its settling releases only its own. */
  claim?: Claim;
}

/** A call that holds the key: `ready` settles `true` when its `onMutate` is done, `false` when that threw (the claim is gone). */
interface Claim {
  ready: Promise<boolean>;
}

/** The two maps of one mutation: the request in flight per key, and the call that holds the callbacks of a key. */
interface OnceState {
  inFlight: Map<string, Promise<unknown>>;
  claims: Map<string, Claim>;
}

export const newOnceState = (): OnceState => ({ inFlight: new Map(), claims: new Map() });

const isPlain = (value: object): boolean => {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

/** The text of `value`, or undefined when it has no stable identity (a function, a class instance, a Blob, a Map, a cycle). */
function describe(value: unknown, seen: object[]): string | undefined {
  if (value === undefined) return 'undefined';
  // JSON.stringify writes NaN and Infinity as `null`, which would make `{n: NaN}` the same call as `{n: null}`.
  if (typeof value === 'number' && !Number.isFinite(value)) return String(value);
  if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return JSON.stringify(value);
  if (typeof value !== 'object') return undefined;
  if (seen.includes(value)) return undefined;
  const path = [...seen, value];
  if (Array.isArray(value)) {
    const items = value.map((v) => describe(v, path));
    return items.includes(undefined) ? undefined : `[${items.join(',')}]`;
  }
  if (!isPlain(value)) return undefined;
  const entries: string[] = [];
  for (const k of Object.keys(value).sort()) {
    const text = describe((value as Record<string, unknown>)[k], path);
    if (text === undefined) return undefined;
    entries.push(`${JSON.stringify(k)}:${text}`);
  }
  return `{${entries.join(',')}}`;
}

/**
 * The identity of a call: its variables written with sorted keys, so `{a, b}` and `{b, a}` are one call and `{a: undefined}` is not
 * `{}`. Variables with no stable identity (a FormData, a Blob, a File, a Map, a Date, a function, a cycle) have NO key (undefined):
 * such a call is never shared and holds no claim, so nothing collapses and nothing is left to release.
 */
export function onceKey(variables: unknown): string | undefined {
  return describe(variables, []);
}

/**
 * Sends once while one is in flight for `key`; a second call gets the first call's promise. The map is written synchronously and
 * cleared when the request settles, success or failure, so a later call sends again. `onSettle` runs at the same moment.
 */
export function sendOnce<T>(inFlight: Map<string, Promise<unknown>>, key: string, send: () => Promise<T>, onSettle?: () => void): Promise<T> {
  const running = inFlight.get(key);
  if (running) return running as Promise<T>;
  // `new Promise` catches a synchronous throw of `send`, so the entry is always released.
  const call = new Promise<T>((resolve) => resolve(send())).finally(() => {
    inFlight.delete(key);
    onSettle?.();
  });
  inFlight.set(key, call);
  return call;
}

/** Beyond the guard. */
interface OnceExtras<TData, TVariables> {
  /**
   * What the hook was made for and the variables do not say: the ids a hook takes as ARGUMENTS (`useRebuildLoadBalancer(serviceId, id)`
   * is called with `mutate()`). The guard lives in the component that holds the hook; React Router keeps that component mounted when
   * only the route's `:id` changes, so without these a click on load balancer B while A's request is in flight would share A's.
   */
  keyExtra?: readonly unknown[];
  /**
   * What makes two calls the same request, when that is less than all the variables (a charge is the same charge for one invoice and
   * amount whichever saved card was clicked). The result replaces the variables in the key; it must be plain data (see {@link onceKey}).
   */
  keyOf?: (variables: TVariables) => unknown;
  /** Runs on the success of EVERY call, sender or not (a list refresh costs nothing and a second caller still wants it). */
  onSuccessEveryCall?: (data: TData, variables: TVariables) => void;
}

/**
 * `options` with a once-guard: two clicks on one set of variables share one request, and only the call that sent runs the caller's
 * `onMutate`, `onSuccess`, `onError` and `onSettled`, so a double click on a failing request gives one error toast, not two.
 *
 * The first `onMutate` of a key claims it (a later call waits for the claimant's `onMutate` to finish, so the one that sent is
 * also the one that sends). The claim is released when the REQUEST settles, not when the callbacks do: a click during a slow
 * `onSuccess` is a new call with its own request and its own callbacks. If the caller's `onMutate` throws, the claim is released
 * at once, so the next click is not a silent non-sender.
 */
export function onceMutationOptions<TData, TError, TVariables, TContext = unknown>(
  state: OnceState,
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
  extras: OnceExtras<TData, TVariables> = {},
): UseMutationOptions<TData, TError, TVariables, OnceCall<TContext>> {
  const { inFlight, claims } = state;
  const { mutationFn } = options;
  /** The key of a call: the hook's arguments, then the variables; undefined when either has no stable identity. */
  const keyOf = (variables: TVariables): string | undefined => {
    const extra = extras.keyExtra ? onceKey([...extras.keyExtra]) : '';
    const own = onceKey(extras.keyOf ? extras.keyOf(variables) : variables);
    return extra === undefined || own === undefined ? undefined : `${extra}|${own}`;
  };
  return {
    ...options,
    mutationFn:
      mutationFn &&
      ((variables, context) => {
        const key = keyOf(variables);
        if (key === undefined) return Promise.resolve(mutationFn(variables, context));
        return sendOnce(inFlight, key, () => Promise.resolve(mutationFn(variables, context)), () => claims.delete(key));
      }),
    onMutate: async (variables, context) => {
      const key = keyOf(variables);
      if (key === undefined) return { sender: true, context: await options.onMutate?.(variables, context) };
      for (let claim = claims.get(key); claim; claim = claims.get(key)) {
        // The claimant's onMutate must finish before this call goes on, or this call's request could go out first. If it threw, the claim is gone and this call takes over.
        if (await claim.ready) return { sender: false, context: undefined };
        if (claims.get(key) === claim) claims.delete(key);
      }
      let finish: (ok: boolean) => void = () => {};
      const claim: Claim = { ready: new Promise<boolean>((resolve) => (finish = resolve)) };
      claims.set(key, claim);
      try {
        const returned = await options.onMutate?.(variables, context);
        finish(true);
        return { sender: true, context: returned, claim };
      } catch (error) {
        if (claims.get(key) === claim) claims.delete(key);
        finish(false);
        throw error;
      }
    },
    onSuccess: (data, variables, call, context) => {
      extras.onSuccessEveryCall?.(data, variables);
      if (call?.sender) return options.onSuccess?.(data, variables, call.context as TContext, context);
    },
    onError: (error, variables, call, context) => {
      // No call: the caller's own onMutate threw, and only the sender runs it.
      if (!call || call.sender) return options.onError?.(error, variables, call?.context as TContext, context);
    },
    onSettled: (data, error, variables, call, context) => {
      // A claim whose request never went out (no mutationFn, or it never ran) is released here; one a newer call took over is left alone.
      const key = keyOf(variables);
      if (key !== undefined && call?.claim && claims.get(key) === call.claim && !inFlight.has(key)) claims.delete(key);
      if (!call || call.sender) return options.onSettled?.(data, error, variables, call?.context as TContext, context);
    },
  };
}

/** `useMutation` with the once-guard: the drop-in for a mutation behind a confirm or a submit that must not be sent twice. */
export function useOnceMutation<TData, TError, TVariables, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
  extras?: OnceExtras<TData, TVariables>,
) {
  const state = useRef<OnceState>(newOnceState());
  return useMutation(onceMutationOptions(state.current, options, extras));
}

/**
 * For a caller that `await`s `mutateAsync(...)` and then toasts or closes: the code after the await runs for EVERY call, because
 * `mutateAsync` resolves for the call that shared the request too. `runOnce` lets one run through while it is going and ignores the
 * rest (read from a ref, not from `isPending`, which is stale until the next render). It does not replace the guard: it is for the code around it.
 */
export function runOnce<T>(busy: { current: boolean }, run: () => Promise<T>): Promise<T | undefined> {
  if (busy.current) return Promise.resolve(undefined);
  busy.current = true;
  let call: Promise<T>;
  try {
    call = run();
  } catch (error) {
    // A synchronous throw of `run` would otherwise leave `busy` true for good.
    busy.current = false;
    return Promise.reject(error);
  }
  return call.finally(() => {
    busy.current = false;
  });
}

/** {@link runOnce} with its own ref: `const once = useRunOnce(); async function submit() { await once(async () => { ... }); }`. */
export function useRunOnce() {
  const busy = useRef(false);
  return useCallback(<T,>(run: () => Promise<T>) => runOnce(busy, run), []);
}
